import React, { useCallback, useEffect, useState } from "react";
import { Timer, Save, Plus, Trash2 } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-[#9CA3AF]">{hint}</div>}
    </div>
  );
}

export default function CallbackSlaSettings() {
  const [urgent, setUrgent] = useState(15);
  const [normal, setNormal] = useState(240);
  const [overrides, setOverrides] = useState([]); // [{facility_id, urgent_minutes, normal_minutes}]
  const [facilities, setFacilities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sla, fac] = await Promise.all([
        api.get("/callbacks/sla-settings"),
        api.get("/facilities").catch(() => ({ data: { items: [] } })),
      ]);
      setUrgent(sla.data.urgent_minutes ?? 15);
      setNormal(sla.data.normal_minutes ?? 240);
      const rawOverrides = sla.data.facility_overrides || {};
      setOverrides(Object.entries(rawOverrides).map(([fid, v]) => ({
        facility_id: fid,
        urgent_minutes: v.urgent_minutes ?? sla.data.urgent_minutes,
        normal_minutes: v.normal_minutes ?? sla.data.normal_minutes,
      })));
      setFacilities(fac.data.items || fac.data.facilities || fac.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  function addOverride() {
    const used = new Set(overrides.map((o) => o.facility_id));
    const next = facilities.find((f) => !used.has(f.id));
    if (!next) { toast.error("No more facilities to override."); return; }
    setOverrides([...overrides, { facility_id: next.id, urgent_minutes: urgent, normal_minutes: normal }]);
  }
  function updateOverride(i, patch) {
    setOverrides(overrides.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  }
  function removeOverride(i) {
    setOverrides(overrides.filter((_, idx) => idx !== i));
  }

  async function save() {
    if (urgent < 1 || normal < 1) { toast.error("SLA windows must be at least 1 minute."); return; }
    if (urgent > 1440 || normal > 10080) { toast.error("SLA windows are too long."); return; }
    for (const o of overrides) {
      if (!o.urgent_minutes || !o.normal_minutes || o.urgent_minutes < 1 || o.normal_minutes < 1) {
        toast.error("Every facility override must have positive urgent + normal minutes."); return;
      }
    }
    setSaving(true);
    try {
      const facilityOverrides = {};
      overrides.forEach((o) => {
        facilityOverrides[o.facility_id] = {
          urgent_minutes: Number(o.urgent_minutes),
          normal_minutes: Number(o.normal_minutes),
        };
      });
      await api.put("/callbacks/sla-settings", {
        urgent_minutes: Number(urgent),
        normal_minutes: Number(normal),
        facility_overrides: facilityOverrides,
      });
      toast.success("Callback SLA settings saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  const facilityName = (id) => facilities.find((f) => f.id === id)?.name || id;

  return (
    <div data-testid="callback-sla-settings-page">
      <PageHeader
        title="Callback SLA Settings"
        subtitle="How long an assignee has to acknowledge a callback before it's escalated to the facility manager and H@H Care Coordinator."
        icon={Timer}
      />
      {loading ? (
        <div className="text-sm text-[#6B7280]">Loading…</div>
      ) : (
        <div className="hah-card p-6 max-w-3xl space-y-6">
          <h3 className="text-base font-semibold text-[#1F2937]">Platform defaults</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Urgent (minutes)" hint="Time before an urgent callback escalates.">
              <Input type="number" min="1" max="1440" value={urgent} onChange={(e) => setUrgent(e.target.value)}
                data-testid="sla-urgent-input" />
            </Field>
            <Field label="Normal (minutes)" hint="Time before a normal callback escalates.">
              <Input type="number" min="1" max="10080" value={normal} onChange={(e) => setNormal(e.target.value)}
                data-testid="sla-normal-input" />
            </Field>
          </div>

          <div className="border-t border-[#E5E7EB] pt-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-semibold text-[#1F2937]">Facility overrides</h3>
              <Button type="button" size="sm" onClick={addOverride} disabled={overrides.length >= facilities.length}
                className="bg-[#0B7E8C] hover:bg-[#128C97] text-white h-8" data-testid="sla-add-override">
                <Plus className="w-4 h-4 mr-1" /> Add override
              </Button>
            </div>
            {overrides.length === 0 ? (
              <div className="text-sm text-[#6B7280]">No overrides. Every facility uses the defaults above.</div>
            ) : (
              <div className="space-y-2">
                {overrides.map((o, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end p-3 border border-[#E5E7EB] rounded-md"
                    data-testid={`sla-override-row-${i}`}>
                    <Field label="Facility">
                      <Select value={o.facility_id} onValueChange={(v) => updateOverride(i, { facility_id: v })}>
                        <SelectTrigger data-testid={`sla-override-facility-${i}`}><SelectValue>{facilityName(o.facility_id)}</SelectValue></SelectTrigger>
                        <SelectContent>
                          {facilities.map((f) => (
                            <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Urgent (min)">
                      <Input type="number" min="1" max="1440" value={o.urgent_minutes}
                        onChange={(e) => updateOverride(i, { urgent_minutes: e.target.value })}
                        data-testid={`sla-override-urgent-${i}`} />
                    </Field>
                    <Field label="Normal (min)">
                      <Input type="number" min="1" max="10080" value={o.normal_minutes}
                        onChange={(e) => updateOverride(i, { normal_minutes: e.target.value })}
                        data-testid={`sla-override-normal-${i}`} />
                    </Field>
                    <Button variant="outline" onClick={() => removeOverride(i)}
                      className="border-[#E5E7EB] text-red-600 hover:bg-red-50 h-9" data-testid={`sla-override-remove-${i}`}>
                      <Trash2 className="w-4 h-4 mr-1" /> Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving}
              className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="sla-save">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save SLA settings"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
